/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Box, Button, FlexContainer } from "@nordcloud/gnui";
import { Provider } from "~/generated/graphql";
import {
  LoaderWrap,
  Pagination,
  SortSelector,
  SortDirection,
} from "~/components";
import { dateFormat } from "~/constants";
import { useScrollAfterPaging, useQueryState, useDisclosure } from "~/hooks";
import { reverse } from "~/tools";
import {
  SELECT_OPTIONS,
  SORT_FILTERS,
} from "~/views/businessContexts/OrganizationUnit/Lists/ApplicationList/constants";
import { getApplications } from "~/views/businessContexts/OrganizationUnit/Lists/ApplicationList/utils";
import { AttachToEnv } from "../AttachToEnv";
import { ApplicationItem } from "./ApplicationItem";
import { useMappedApplications } from "./mapping";
import { getResources } from "./utils";

type Props = {
  recordNid: string;
  refetch?: () => void;
  resourceProvider?: Provider;
};
export function ApplicationsList({
  recordNid,
  refetch,
  resourceProvider,
}: Props) {
  const scrollToRef = React.useRef<HTMLDivElement>(null);
  const {
    state: { page, limit },
  } = useQueryState();
  const [activeFilter, setActiveFilter] = React.useState<SORT_FILTERS>(
    SORT_FILTERS.app_name
  );
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(
    SortDirection.Ascending
  );

  const {
    isOpen: attachmentSidebarOpen,
    close: closeAttachmentSidebar,
    open: openAttachmentSidebar,
  } = useDisclosure();

  const {
    applications,
    count,
    loading,
    refetch: refetchApps,
  } = useMappedApplications({
    limit,
    page,
    estateRecordNid: recordNid,
    year: dayjs().format(dateFormat.year),
  });

  const filteredApplications = getApplications(applications, activeFilter);

  const sortedApplications =
    sortDirection === SortDirection.Descending
      ? reverse(filteredApplications)
      : filteredApplications;

  const onAttachmentUpdate = () => {
    void refetchApps();
    refetch?.();
  };

  useScrollAfterPaging({ scrollToRef, enabled: !loading });

  return (
    <Box data-testid="applications-list-estate-record">
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="space-between">
          <SortSelector
            options={SELECT_OPTIONS}
            value={activeFilter}
            direction={sortDirection}
            onSelect={(filter) => setActiveFilter(filter as SORT_FILTERS)}
            onSortChange={setSortDirection}
          />
          <Button
            severity="medium"
            icon="link"
            size="md"
            onClick={openAttachmentSidebar}
          >
            Attach to different environment
          </Button>
          <AttachToEnv
            allowMultipleAssignment
            resources={getResources(recordNid, resourceProvider)}
            refresh={onAttachmentUpdate}
            isOpen={attachmentSidebarOpen}
            onClose={closeAttachmentSidebar}
          />
        </FlexContainer>
      </Box>
      <LoaderWrap inContent loading={loading}>
        {sortedApplications.map((application) => (
          <ApplicationItem key={application.id} application={application} />
        ))}
        <Pagination count={count} />
      </LoaderWrap>
    </Box>
  );
}
